/**
 * URS-F329 AutoAssignmentRulesPanel
 *
 * Panel para configurar reglas de auto-asignacion de staff por
 * `product_type` al crear un estudio.
 *
 * Tabla con columnas: Rol, Tipos de producto, Usuario asignado, Acciones.
 * Botones: Anadir, Editar, Eliminar.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'director_estudio', label_es: 'Director de Estudio', label_en: 'Study Director' },
  { value: 'responsable_sondas', label_es: 'Responsable de Sondas', label_en: 'Probes Lead' },
  { value: 'responsable_reclutamiento', label_es: 'Responsable de Reclutamiento', label_en: 'Recruitment Lead' },
  { value: 'investigador_principal', label_es: 'Investigador Principal', label_en: 'PI' },
  { value: 'coordinador', label_es: 'Coordinador', label_en: 'Coordinator' },
  { value: 'monitor', label_es: 'Monitor', label_en: 'Monitor' },
  { value: 'data_manager', label_es: 'Data Manager', label_en: 'Data Manager' },
  // URS-F431: rol especifico de estudios dentales.
  // Solo se muestra el slot en la ficha del estudio cuando isDentalStudy=true,
  // por lo que emparejar esta regla con product_types no-dentales no tendra
  // efecto visible en la UI (el usuario tendria que asignar la regla al
  // product_type correspondiente: cosmetico/cosmetico_plus con phase Dental
  // o sanitario con phase Odontologia).
  { value: 'aux_odontologia', label_es: 'Aux Odontologia', label_en: 'Dental Assistant' },
];
const PRODUCT_TYPE_OPTIONS = [
  { value: 'cosmetico', label_es: 'Cosmético', label_en: 'Cosmetic' },
  { value: 'cosmetico_plus', label_es: 'Cosmético plus', label_en: 'Cosmetic plus' },
  { value: 'sanitario', label_es: 'Producto sanitario', label_en: 'Medical device' },
  { value: 'suplemento', label_es: 'Suplemento alimenticio', label_en: 'Food supplement' },
  { value: 'medicamento', label_es: 'Medicamento', label_en: 'Drug' },
];

// URS-F336/F337: roles globales que cuadran con cada slot del staff. Mismo
// criterio que `ROLE_INCLUDES` en `StudyPanels.js`.
const ROLE_INCLUDES = {
  director_estudio: ['director_estudio', 'admin'],
  responsable_sondas: ['supervisor_sondas'],
  responsable_reclutamiento: ['supervisor_reclutamiento'],
  investigador_principal: ['investigador_principal'],
  investigador: ['investigador'],
  coordinador: ['coordinador'],
  monitor: ['monitor'],
  data_manager: ['data_manager'],
  // URS-F431: emparejar con usuarios que tengan el rol global "odontologia".
  aux_odontologia: ['odontologia'],
};

// URS-F337: nombre completo abreviado = "Nombre + Primer apellido".
// Los usuarios tienen los apellidos en campos separados (`surname1`,
// `surname2`), por lo que el primer intento (parsear `name`) era erroneo.
// Aceptamos tanto un string suelto (rules.user_name cacheado) como un
// objeto user con campos {name, surname1}.
const shortDisplayName = (userOrName) => {
  if (!userOrName) return '';
  // Caso A: objeto user con name + surname1
  if (typeof userOrName === 'object') {
    const n = (userOrName.name || '').trim();
    const s1 = (userOrName.surname1 || '').trim();
    if (n && s1) return `${n} ${s1}`;
    if (n) return n;
    return userOrName.email || '';
  }
  // Caso B: string con todo concatenado (fallback). Quitamos el ultimo token
  // si el nombre tiene >=3 tokens (convencion espanola).
  const parts = String(userOrName).trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return parts.join(' ');
  return parts.slice(0, -1).join(' ');
};

export default function AutoAssignmentRulesPanel({ lang = 'es' }) {
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | rule object
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rs, us] = await Promise.all([
        axios.get(`${API_URL}/api/config/auto-assignment-rules`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
      ]);
      setRules(rs.data || []);
      setUsers((us.data || []).filter(u => u.is_active !== false));
    } catch (e) {
      toast.error(lang === 'es' ? 'Error cargando reglas' : 'Error loading rules');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => setEditing({ rule_id: null, role: 'director_estudio', product_types: [], user_id: '' });
  const openEdit = (r) => setEditing({ ...r, product_types: [...r.product_types] });

  const togglePT = (pt) => {
    setEditing(p => ({
      ...p,
      product_types: p.product_types.includes(pt)
        ? p.product_types.filter(x => x !== pt)
        : [...p.product_types, pt],
    }));
  };

  const save = async () => {
    if (!editing.role || !editing.user_id || editing.product_types.length === 0) {
      toast.error(lang === 'es' ? 'Completa todos los campos' : 'Complete all fields');
      return;
    }
    try {
      if (editing.rule_id) {
        await axios.put(`${API_URL}/api/config/auto-assignment-rules/${editing.rule_id}`, {
          role: editing.role, user_id: editing.user_id, product_types: editing.product_types,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/config/auto-assignment-rules`, {
          role: editing.role, user_id: editing.user_id, product_types: editing.product_types,
        }, { withCredentials: true });
      }
      toast.success(lang === 'es' ? 'Regla guardada' : 'Rule saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al guardar' : 'Error saving'));
    }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/config/auto-assignment-rules/${confirmDelete.rule_id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Regla eliminada' : 'Rule deleted');
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(lang === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const roleLabel = (key) => {
    const o = ROLE_OPTIONS.find(x => x.value === key);
    return o ? (lang === 'es' ? o.label_es : o.label_en) : key;
  };
  const ptLabel = (key) => {
    const o = PRODUCT_TYPE_OPTIONS.find(x => x.value === key);
    return o ? (lang === 'es' ? o.label_es : o.label_en) : key;
  };

  return (
    <div className="space-y-4" data-testid="auto-assignment-rules-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-stone-900">{lang === 'es' ? 'Reglas de auto-asignación' : 'Auto-assignment rules'}</h3>
          <p className="text-xs text-stone-500 mt-1 max-w-2xl">
            {lang === 'es'
              ? 'Al crear un estudio nuevo, se asigna automáticamente al usuario indicado en el rol del staff seleccionado, según el tipo de producto del estudio. Útil, por ejemplo, para que los estudios cosméticos lleven siempre el mismo director.'
              : 'When a new study is created, the selected user is automatically added to the chosen staff role based on the study product type.'}
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-auto-rule-btn" className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2 rounded-sm">
          <Plus size={14} />{lang === 'es' ? 'Nueva regla' : 'New rule'}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-stone-400 text-center py-6">{lang === 'es' ? 'Cargando…' : 'Loading…'}</p>
      ) : rules.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-6 border border-dashed border-stone-200 rounded-sm">
          {lang === 'es' ? 'No hay reglas configuradas. Crea una para que los estudios nuevos auto-asignen staff por tipo de producto.' : 'No rules configured.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">{lang === 'es' ? 'Rol' : 'Role'}</TableHead>
              <TableHead>{lang === 'es' ? 'Tipos de producto' : 'Product types'}</TableHead>
              <TableHead className="w-56">{lang === 'es' ? 'Usuario asignado' : 'Assigned user'}</TableHead>
              <TableHead className="w-24">{lang === 'es' ? 'Acciones' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.map(r => (
              <TableRow key={r.rule_id} data-testid={`auto-rule-${r.rule_id}`}>
                <TableCell className="font-medium text-stone-800">{roleLabel(r.role)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.product_types.map(pt => (
                      <Badge key={pt} variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{ptLabel(pt)}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-stone-700">{shortDisplayName(r.user_name) || r.user_name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-[#1f3e80]" onClick={() => openEdit(r)} data-testid={`edit-rule-${r.rule_id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" onClick={() => setConfirmDelete(r)} data-testid={`delete-rule-${r.rule_id}`}><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Editor modal */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="auto-rule-editor">
          <DialogHeader>
            <DialogTitle>{editing?.rule_id ? (lang === 'es' ? 'Editar regla' : 'Edit rule') : (lang === 'es' ? 'Nueva regla' : 'New rule')}</DialogTitle>
            <DialogDescription className="text-xs">
              {lang === 'es' ? 'Define qué usuario y rol se asigna automáticamente al crear un estudio del tipo seleccionado.' : 'Define which user and role are auto-assigned when creating a study of the selected product type.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs mb-1">{lang === 'es' ? 'Rol del staff' : 'Staff role'}</Label>
              <Select value={editing?.role || ''} onValueChange={(v) => setEditing(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="rule-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{lang === 'es' ? o.label_es : o.label_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1">{lang === 'es' ? 'Usuario a asignar' : 'User to assign'}</Label>
              {(() => {
                const acceptedRoles = ROLE_INCLUDES[editing?.role] || [editing?.role];
                const filtered = users.filter(u => (u.roles || []).some(r => acceptedRoles.includes(r)));
                return (
                  <>
                    <Select value={editing?.user_id || ''} onValueChange={(v) => setEditing(p => ({ ...p, user_id: v }))}>
                      <SelectTrigger data-testid="rule-user-select"><SelectValue placeholder={lang === 'es' ? 'Selecciona…' : 'Select…'} /></SelectTrigger>
                      <SelectContent>
                        {filtered.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-stone-400">
                            {lang === 'es' ? 'Ningún usuario con este rol.' : 'No users with this role.'}
                          </div>
                        ) : filtered.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {shortDisplayName(u) || u.email}
                            {u.specialty ? ` · ${u.specialty}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-stone-400 mt-1">
                      {lang === 'es'
                        ? `Solo se muestran usuarios con el rol global "${roleLabel(editing?.role)}".`
                        : `Only users with the "${roleLabel(editing?.role)}" global role are shown.`}
                    </p>
                  </>
                );
              })()}
            </div>
            <div>
              <Label className="text-xs mb-1">{lang === 'es' ? 'Tipos de producto' : 'Product types'}</Label>
              <div className="flex flex-wrap gap-1.5">
                {PRODUCT_TYPE_OPTIONS.map(o => {
                  const active = editing?.product_types?.includes(o.value);
                  return (
                    <button
                      type="button"
                      key={o.value}
                      onClick={() => togglePT(o.value)}
                      data-testid={`rule-pt-${o.value}`}
                      className={`text-xs px-2 py-1 rounded-sm border transition-colors ${active ? 'bg-violet-100 border-violet-300 text-violet-700' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'}`}
                    >
                      {lang === 'es' ? o.label_es : o.label_en}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={save} data-testid="save-rule-btn" className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">
              {lang === 'es' ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Eliminar regla' : 'Delete rule'}</DialogTitle>
            <DialogDescription>
              {lang === 'es'
                ? `¿Eliminar la regla que asigna a ${confirmDelete?.user_name} como ${roleLabel(confirmDelete?.role || '')} en estudios de tipo ${(confirmDelete?.product_types || []).map(ptLabel).join(', ')}?`
                : 'Are you sure?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-rule-btn">
              {lang === 'es' ? 'Eliminar' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
